import { api, request, requireAccount, withAccount } from "./core";

export interface Repository {
  id: number;
  ex_github_id: number;
  account_id: number;
  full_name: string;
  domain: string;
  is_active: boolean;
  is_private: boolean; // drives the size cap (public repos are never capped)
  clone_status: string; // none | cloned | deleting
  clone_size_bytes: number;
  last_cloned_at: string | null;
  // Soft-delete timestamp. non-null = removed from the list (a repo gone on
  // GitHub the user retired). The default list excludes these; ?removed=true
  // returns only them. The row + its observations are kept (D-07).
  removed_at: string | null;
}

export interface GitHubRepo {
  id: number;
  full_name: string;
  private: boolean;
  language: string;
  fork: boolean;
  archived: boolean;
  size?: number; // GitHub repo size in KB (drives the free-plan size cap); absent for DB-only rows
  languages?: Record<string, number>;
}

export const repoApi = {
  // list returns the galaxy's live (non-removed) repos. Pass {removed:true} to
  // get the soft-deleted ones instead (for the "removed" filter / restore UI).
  list: (accountLogin: string, opts?: { removed?: boolean }) =>
    api.get<Repository[]>(
      withAccount("/repos", requireAccount(accountLogin, "repoApi.list"), opts?.removed ? { removed: "true" } : undefined),
    ),
  // oversize_active: count of ON repos that exceed the plan size cap (skipped by
  // the worker). Drives the sidebar warning on the Repositories nav item.
  health: (accountLogin: string) =>
    api.get<{ oversize_active: number }>(withAccount("/repos/health", requireAccount(accountLogin, "repoApi.health"))),
  listGitHub: (accountLogin: string, refresh = false) =>
    api.get<GitHubRepo[]>(withAccount("/repos/github", requireAccount(accountLogin, "repoApi.listGitHub"), refresh ? { refresh: "true" } : undefined)),
  register: (data: { ex_github_id: number; full_name: string }, accountLogin: string) =>
    api.post<Repository>(withAccount("/repos", requireAccount(accountLogin, "repoApi.register")), data),
  deactivate: (repoId: number, accountLogin: string) =>
    request<void>(withAccount("/repos", requireAccount(accountLogin, "repoApi.deactivate")), { method: "DELETE", body: JSON.stringify({ repo_id: repoId }) }),
  updateDomain: (repoId: number, domain: string, accountLogin: string) =>
    api.put<{ status: string }>(withAccount("/repos/domain", requireAccount(accountLogin, "repoApi.updateDomain")), { repo_id: repoId, domain }),
  // remove soft-deletes a repo gone on GitHub so it drops out of the list (the
  // row + observations are kept, D-07). restore puts it back. Both admin-only.
  remove: (repoId: number, accountLogin: string) =>
    request<void>(withAccount("/repos/remove", requireAccount(accountLogin, "repoApi.remove")), { method: "POST", body: JSON.stringify({ repo_id: repoId }) }),
  restore: (repoId: number, accountLogin: string) =>
    request<void>(withAccount("/repos/restore", requireAccount(accountLogin, "repoApi.restore")), { method: "POST", body: JSON.stringify({ repo_id: repoId }) }),
  deleteClone: (repoId: number, accountLogin: string) =>
    request<{ status: string }>(withAccount("/repos/clone", requireAccount(accountLogin, "repoApi.deleteClone")), { method: "DELETE", body: JSON.stringify({ repo_id: repoId }) }),
};
